import { LitElement, html, css, unsafeCSS } from 'lit';
import { DEFAULT_CERTIFICATE_FORMAT } from '@mv/models';
import { atmisOrderUrl, labPresentation, labStatusPresentatie, MONSTERTYPES } from '../labs.js';
import { FASES, FASE_VOLGORDE, faseVan, faseMomenten, isAfgekeurd, conceptVoortgang, telFases } from '../utils/lab-fase.js';
import './searchable-select.js';
import './column-picker.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, ExternalLink, FileText, FlaskConical, Mail, Plus,
         RefreshCw, Search, X } from 'lucide';
import { api } from '../api.js';
import './filter-keuze.js';
import { formatDateTime, formatTime, parseTimestamp } from '../utils/datetime.js';
import { auth } from '../auth.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { lucideIcon } from '../icon.js';
import base from '../styles/base.css?inline';

/**
 * lab-koppeling-page — de lijst met labopdrachten.
 *
 * Twee plekken, een component: met een `projectId` toont hij de opdrachten van dat project
 * (het tabblad Labopdrachten), zonder die van het hele bedrijf (de hoofdpagina). De kolommen
 * zijn dezelfde, en dat is de bedoeling -- het verschil zit in wat je ziet, niet in hoe het
 * eruitziet.
 *
 * Een opdracht openen gaat naar /labopdrachten/:id, een eigen pagina. Ze stonden hier eerst
 * als detailvenster in, waardoor je twee menu's tegelijk zag en een opdracht geen eigen adres
 * had om te delen.
 */

function formatDate(value) {
  // parseTimestamp leest een kale tijd uit de database als UTC; new Date() deed dat als lokale
  // tijd en zette de datum rond middernacht een dag mis.
  const d = parseTimestamp(value);
  if (!d) return '-';
  return d.toLocaleDateString('nl-NL');
}

export class LabKoppelingPage extends LitElement {
  /**
   * De kolommen van de tabel met opdrachten buiten GeoVoice: id, kop en breedte in het raster.
   *
   * `vast` betekent dat je hem niet kunt uitzetten: daar zitten de knoppen in waarmee je de
   * uitslag bekijkt en het certificaat opent, en een tabel zonder die kolom is een tabel waar
   * je niets meer mee kunt.
   *
   * De eigen opdrachten hebben vaste kolommen (zie _renderEigenTabel): de fase en de volgende
   * stap zijn het hele punt van die tabel, en wat er verder staat is kort genoeg om altijd te
   * passen.
   */
  static EXTERN_KOLOMMEN = [
    { id: 'order', label: 'Ordernummer', breedte: '110px' },
    { id: 'project', label: 'Project', breedte: 'minmax(110px,180px)' },
    { id: 'projectLeader', label: 'Projectleider', breedte: 'minmax(100px,150px)' },
    { id: 'date', label: 'Datum', breedte: '100px' },
    { id: 'labStatus', label: 'Labstatus', breedte: '125px' },
    { id: 'batch', label: 'Batchnummer', breedte: '110px' },
    { id: 'batchStatus', label: 'Batchstatus', breedte: '125px' },
    { id: 'results', label: 'Resultaten', breedte: 'minmax(215px,1fr)', vast: true },
  ];

  static EXTERN_KOLOMMEN_PREF = 'columns.labOrdersExtern';

  /** Alles aan, tenzij een kolom zichzelf op `standaard: false` zet. */
  static _standaard(lijst) {
    return lijst.filter(k => k.standaard !== false).map(k => k.id);
  }

  static properties = {
    /** Leeg = alle labopdrachten van het bedrijf. */
    projectId: { type: String },
    project: { type: Object },
    _loading: { state: true },
    _error: { state: true },
    _connections: { state: true },
    _orders: { state: true },
    _counts: { state: true },
    _query: { state: true },
    _projectFilter: { state: true },  // ids van de gekozen projecten; leeg = alle projecten
    _fase: { state: true },          // 'alle' of een sleutel uit FASES: het tabblad dat aan staat
    _newOpen: { state: true },
    _projecten: { state: true },
    _externOpen: { state: true },
    _externQuery: { state: true },
    _externFilter: { state: true },
    _periode: { state: true },
    _externKolommen: { state: true },
    _newForm: { state: true },
    _saving: { state: true },
    _license: { state: true },
    _labStatussen: { state: true },  // wat er bij het lab ligt, uit de laatste ronde
    _haalt: { state: true },
    _resultaatVoor: { state: true }, // het opdrachtnummer waarvan de uitslag openstaat
    _resultaatBezig: { state: true },
    _certBezig: { state: true },
    _haalfout: { state: true },
    _opgehaaldOp: { state: true },
    // Staat het herlaadknopje op slot? Zie _verversNu: een tel of tien na een klik, zodat
    // dertig klikken geen dertig gesprekken met het lab worden.
    _verversOpSlot: { state: true },
  };

  constructor() {
    super();
    this.project = null;
    this._loading = true;
    this._error = null;
    this._connections = [];
    this._orders = [];
    this._counts = {};
    this._query = '';
    this._projectFilter = [];
    this._fase = 'alle';
    this._newOpen = false;
    this._projecten = [];
    // Standaard dicht: het is een naslaglijst, geen werk. Wie hem nodig heeft, klapt hem open.
    this._externOpen = false;
    this._externQuery = '';
    this._externFilter = 'all';
    // Twaalf maanden: de statuslijst van SGS vraagt om een venster in dagen, en 60 was te kort
    // -- bij een gedeelde testklantcode staan er honderden opdrachten die ouder zijn.
    this._periode = 365;
    this._externKolommen = LabKoppelingPage._standaard(LabKoppelingPage.EXTERN_KOLOMMEN);
    this._newForm = {};
    this._saving = false;
    this._license = null;
    this._labStatussen = [];
    this._haalt = false;
    this._resultaatVoor = null;
    this._resultaatBezig = '';
    this._certBezig = '';
    this._haalfout = '';
    this._opgehaaldOp = '';
    this._verversOpSlot = false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Oude links (?order=<id>) wezen naar het detailvenster dat hier in zat. Die brengen we
    // naar het nieuwe adres in plaats van ze stil te laten landen op de lijst.
    const oud = new URLSearchParams(window.location.search).get('order');
    if (oud) { this._openOrder({ id: oud }); return; }
    this._load();
  }

  disconnectedCallback() {
    clearTimeout(this._slotTimer);
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has('projectId')) this._load();
  }

  /** Geen project = alle labopdrachten van het bedrijf. */
  /** Wat bij het lab ligt maar niet uit GeoVoice komt; op nummer vergeleken. */
  /**
   * De externe opdrachten, gesorteerd en gefilterd.
   *
   * Sorteren op datum lukt alleen als we het resultaatbestand hebben: de statuslijst van SGS
   * draagt er geen (zie docs/sgs-labkoppeling-handoff-claude.md). Zonder datum is het
   * ordernummer het beste alternatief -- die loopt op, dus hoog is nieuw.
   */
  get _externGefilterd() {
    const q = this._externQuery.trim().toLowerCase();
    const lijst = (this._externeStatussen || []).filter((o) => {
      if (this._externFilter !== 'all') {
        const stand = labStatusPresentatie(o.statusName || o.statusId);
        if ((stand?.klasse || 'neutraal') !== this._externFilter) return false;
      }
      if (!q) return true;
      return [o.orderId, o.batchId, o.statusName, o.batchStatusName,
              o.results?.projectCode, o.results?.projectName, o.results?.projectLeader]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
    return lijst.sort((a, b) => {
      const da = a.results?.reportedOn || '';
      const db = b.results?.reportedOn || '';
      if (da && db && da !== db) return db.localeCompare(da);
      return Number(b.orderId || 0) - Number(a.orderId || 0);
    });
  }

  get _externeStatussen() {
    const eigen = new Set((this._orders || []).map(o => String(o.labOrderId || '')).filter(Boolean));
    return (this._labStatussen || []).filter(s => !eigen.has(String(s.orderId)));
  }

  get _bedrijfsbreed() {
    return !this.projectId;
  }

  /**
   * De kolomkeuze van deze gebruiker.
   *
   * Onbekende ids vallen weg: hernoemen we later een kolom, dan verdwijnt die uit de voorkeur
   * in plaats van als spookkolom te blijven hangen. Een lege lijst is een geldige keuze (alles
   * uit), dus we kijken naar het type en niet naar de lengte.
   */
  async _haalKolomvoorkeur() {
    try {
      const prefs = await api.getPreferences();
      const lees = (sleutel, lijst) => {
        const bewaard = prefs?.[sleutel];
        if (!Array.isArray(bewaard)) return null;
        const bekend = new Set(lijst.map(k => k.id));
        const vast = lijst.filter(k => k.vast).map(k => k.id);
        return [...new Set([...bewaard.filter(id => bekend.has(id)), ...vast])];
      };
      const extern = lees(LabKoppelingPage.EXTERN_KOLOMMEN_PREF, LabKoppelingPage.EXTERN_KOLOMMEN);
      if (extern) this._externKolommen = extern;
    } catch (_) { /* voorkeuren zijn comfort, geen voorwaarde */ }
  }

  /** Bewaren bij elke klik: een knop 'opslaan' zou hier alleen in de weg zitten. */
  async _bewaarKolommen(sleutel, zichtbaar) {
    this._externKolommen = zichtbaar;
    try { await api.setPreference(sleutel, zichtbaar); }
    catch (e) { console.error('kolomvoorkeur opslaan mislukt:', e); }
  }

  /** De zichtbare kolommen, in de vaste volgorde, met hun rasterbreedte. */
  _zichtbareKolommen(lijst, gekozen) {
    return lijst.filter(k => k.vast || gekozen.includes(k.id));
  }

  async _load() {
    this._loading = true;
    this._error = null;
    this._haalKolomvoorkeur();
    try {
      // De licentie alleen op de eigen pagina: in het tabblad heeft project-detail de
      // upgrademelding al, en dan zou hij er twee keer staan.
      if (this._bedrijfsbreed && this._license == null) {
        api.getCompanyProfile()
          .then(p => { this._license = p?.license || 'geovoice'; })
          .catch(() => { this._license = 'geovoice'; });
      }
      const [connections, orders] = await Promise.all([
        api.getActiveLabConnections(),
        this._bedrijfsbreed ? api.getAllLabOrders() : api.getProjectLabOrders(this.projectId),
      ]);
      this._connections = connections?.labConnections || [];
      this._orders = orders?.labOrders || [];
      this._counts = orders?.counts || {};
      this._labStatussen = orders?.labStatuses || [];
      // Het tabblad van een project zet dit aantal naast zijn naam; het project zelf draagt het
      // ook, maar dat getal is van vóór het aanmaken of weggooien van zojuist.
      this.dispatchEvent(new CustomEvent('orders-changed', {
        detail: { count: this._orders.length }, bubbles: true, composed: true,
      }));
      // De lijst staat er; de stand bij het lab komt er zo achteraan. Zie _haalStatussen.
      if (this._bedrijfsbreed && this._connections.length) this._haalStatussen();
    } catch (e) {
      this._error = e.message || 'Labopdrachten laden mislukt';
    } finally {
      this._loading = false;
    }
  }

  /**
   * Een ander venster: opnieuw bij het lab vragen.
   *
   * De rem van tien seconden staat per periode, dus dit levert altijd een verse lijst op --
   * en het herhalen van dezelfde vraag blijft afgeremd.
   */
  _zetPeriode(dagen) {
    if (dagen === this._periode) return;
    this._periode = dagen;
    this._haalStatussen();
  }

  /**
   * De stand van zaken bij het lab ophalen.
   *
   * Gebeurt vanzelf zodra de lijst er staat: je opent deze pagina juist om te zien hoe het
   * ervoor staat, en dan is 'eerst nog ergens op drukken' een omweg. Het gebeurt ná de lijst,
   * want elke aanroep bij SGS kost ruim een seconde en de lijst zelf is er meteen. Het knopje
   * naast de tijd is er voor wie niet wil wachten tot de volgende ronde -- zie _verversNu.
   *
   * De backend remt af: vanzelf hoogstens eens per vijf minuten, op verzoek eens per tien
   * seconden (AUTO_PAUZE_MS / POLL_PAUZE_MS in LabOpdrachtController). Wordt een ronde
   * overgeslagen, dan komt terug wat er al stond, met de tijd waarop dat is opgehaald -- en die
   * tijd staat in beeld, zodat je weet hoe oud het is.
   *
   * Wat terugkomt gaat ook over opdrachten die buiten GeoVoice zijn ingeschoten -- die wil je
   * juist kunnen zien.
   *
   * @param {boolean} opVerzoek  true = het herlaadknopje; zet de pauze van vijf minuten opzij.
   */
  async _haalStatussen(opVerzoek = false) {
    // Geen 'al bezig, dus niets doen': een aanroep duurt een seconde of twee, en wie in die
    // tijd een andere periode kiest zou dan niets zien gebeuren. De laatste vraag wint; een
    // antwoord op een oudere vraag gooien we weg, anders overschrijft die de nieuwe lijst.
    const beurt = (this._beurt || 0) + 1;
    this._beurt = beurt;
    const periode = this._periode;
    this._haalt = true;
    this._haalfout = '';
    try {
      const res = await api.refreshLabOrderStatuses(periode, opVerzoek);
      if (this._beurt !== beurt) return;
      this._orders = res.labOrders || [];
      this._counts = res.counts || {};
      this._labStatussen = res.labStatuses || [];
      this._opgehaaldOp = res.opgehaaldOp || '';
      // Een fout per labkoppeling: die meldingen komen van SGS en zijn hun woorden, dus daar
      // hoort te staan waar ze over gaan.
      this._haalfout = res.fouten?.length
        ? `Het lab gaf een melding terug: ${res.fouten.join(' · ')}`
        : '';
    } catch (e) {
      if (this._beurt !== beurt) return;
      this._haalfout = `De stand bij het lab kon niet worden opgehaald. ${e.message || ''}`.trim();
    } finally {
      if (this._beurt === beurt) this._haalt = false;
    }
  }

  /**
   * Het herlaadknopje naast 'Stand van zaken opgehaald op …'.
   *
   * Twee remmen, om twee verschillende redenen. Die in de backend beschermt de dienst van SGS en
   * is de echte grens. Die hier houdt de knop een tel of tien op slot zodat je ziet dat je klik
   * is aangekomen en niet dertig keer gaat drukken -- puur voor de duidelijkheid, want een rem
   * in de browser is met een herlading zo omzeild.
   */
  async _verversNu() {
    if (this._verversOpSlot || this._haalt) return;
    this._verversOpSlot = true;
    clearTimeout(this._slotTimer);
    this._slotTimer = setTimeout(() => { this._verversOpSlot = false; }, 10_000);
    await this._haalStatussen(true);
  }

  /**
   * Naar de pagina van een opdracht. Waar je vandaan komt gaat mee in de URL, zodat de pijl
   * terug daar weer uitkomt -- ook als je de pagina tussendoor ververst.
   */
  _openOrder(order, e, url = '') {
    e?.preventDefault();
    window.history.pushState({}, '', url || this._orderUrl(order));
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  /**
   * Het adres van een opdracht. Een concept opent op de stap die nog openstaat: wie in de lijst
   * op 'Analyses kiezen' klikt, wil niet eerst langs de administratie.
   */
  _orderUrl(order, stap = '') {
    const params = new URLSearchParams();
    if (this._bedrijfsbreed) params.set('terug', 'alle');
    if (stap) params.set('stap', stap);
    const q = params.toString();
    return `/labopdrachten/${order.id}${q ? `?${q}` : ''}`;
  }

  /** De opdrachten van de gekozen projecten (zonder projectfilter: alle). */
  get _ordersInProjecten() {
    const projecten = this._projectFilter;
    const alle = this._orders || [];
    return projecten.length ? alle.filter(order => projecten.includes(order.projectId)) : alle;
  }

  /** De opdrachten van het gekozen fasetabblad die bij de zoekterm passen. */
  get _filteredOrders() {
    const q = this._query.trim().toLowerCase();
    return this._ordersInProjecten.filter(order => {
      if (this._fase !== 'alle' && faseVan(order) !== this._fase) return false;
      if (!q) return true;
      return [
        order.number,
        order.labOrderId,
        order.labBatchId,
        order.labName,
        order.customerId,
        order.projectLeaderName,
        this._projectVan(order),
        ...(order.badges || []),
      ].some(v => String(v || '').toLowerCase().includes(q));
    });
  }

  /**
   * De stand bij het lab: vertaald, gekleurd, en met uitleg als je hem aanwijst.
   *
   * De namen komen van SGS en zijn Engels ('Busy'), terwijl de rest van het scherm Nederlands
   * is. De uitleg staat er meteen bij -- niet na een seconde zoals bij een `title` -- want de
   * vraag bij zo'n status is altijd dezelfde: moet ik nu iets doen, of het lab?
   */
  _renderLabstatus(order) {
    const stand = labStatusPresentatie(order.labStatusName);
    if (!stand) {
      return html`<span class="labstatus"><em class="leeg">—</em></span>`;
    }
    return html`
      <span class="labstatus" @mousemove=${tipMove} @mouseleave=${tipHide}
            data-tip=${`${order.labName || 'Lab'} · ${stand.label}\n${stand.uitleg}`}>
        <span class="pill ${stand.klasse}">${stand.label}</span>
      </span>`;
  }

  /**
   * De drie keuzes die een labopdracht bepalen worden vooraf gevraagd, niet achteraf op het
   * formulier: lab, monstertype en klantcode staan vast zodra de opdracht bestaat, en het
   * lab bepaalt welke urgenties en afspraken je daarna te zien krijgt.
   */
  _openNewOrder() {
    const first = this._connections[0] || null;
    this._newForm = {
      labConnectionId: first?.id || '',
      sampleType: 'grond',
      labCustomerId: first?.customerId || '',
      // Binnen een project staat het project vast; bedrijfsbreed kies je het erbij.
      projectId: this._bedrijfsbreed ? '' : (this.projectId || ''),
    };
    this._newOpen = true;
    this._error = null;
    if (this._bedrijfsbreed && !this._projecten.length) this._haalProjecten();
  }

  /**
   * De projecten voor de keuzelijst.
   *
   * De backend geeft ze al terug op laatste wijziging, nieuw eerst, en filtert ze op wat je
   * mag zien -- dus geen eigen sortering hier: een project waar je gisteren in werkte staat
   * bovenaan, en dat is bijna altijd degene waarvoor je een labopdracht maakt.
   */
  async _haalProjecten() {
    try {
      this._projecten = await api.getProjects();
    } catch (_) {
      this._projecten = [];
    }
  }

  /** De projecten met labopdrachten, voor het projectfilter: naam, en hoeveel opdrachten. */
  get _projectFilterOpties() {
    const per = new Map();
    for (const o of this._orders || []) {
      if (!o.projectId) continue;
      const p = per.get(o.projectId) || { waarde: o.projectId, label: this._projectVan(o), aantal: 0 };
      p.aantal += 1;
      per.set(o.projectId, p);
    }
    // Twee projecten met dezelfde naam: de projectcode erbij, anders kies je blind.
    const lijst = [...per.values()];
    const dubbel = new Set(lijst.map((p) => p.label).filter((l, i, a) => a.indexOf(l) !== i));
    for (const p of lijst) {
      if (!dubbel.has(p.label)) continue;
      const code = (this._orders || []).find((o) => o.projectId === p.waarde)?.projectCode;
      if (code) p.label = `${p.label} · ${code}`;
    }
    return lijst.sort((a, b) => a.label.localeCompare(b.label, 'nl', { numeric: true }));
  }

  /** De projecten als opties: de naam om op te zoeken, het id als waarde. */
  get _projectOpties() {
    return (this._projecten || []).map(p => ({
      value: p.id,
      label: [p.project_name || p.name || 'Naamloos project', p.project_code].filter(Boolean).join(' · '),
    }));
  }

  /** Van koppeling wisselen betekent een andere klantcode; die volgt dus mee. */
  _setNewLab(labConnectionId) {
    const conn = this._connections.find(c => c.id === labConnectionId) || null;
    this._newForm = {
      ...this._newForm,
      labConnectionId,
      labCustomerId: conn?.customerId || '',
    };
  }

  async _createOrder() {
    if (!this._newForm.labConnectionId) return;
    const projectId = this._newForm.projectId || this.projectId;
    if (!projectId) {
      this._error = 'Kies eerst een project.';
      return;
    }
    this._saving = true;
    this._error = null;
    try {
      const gekozen = (this._projecten || []).find(p => p.id === projectId);
      const projectCode = this.project?.project_code || this.project?.code || this.project?.name
        || gekozen?.project_code || gekozen?.project_name || '';
      const res = await api.createLabOrderDraft(projectId, {
        labConnectionId: this._newForm.labConnectionId,
        reference: projectCode,
        sampleType: this._newForm.sampleType,
        certificateFormat: DEFAULT_CERTIFICATE_FORMAT,
      });
      this._newOpen = false;
      const nieuw = res?.labOrder || res;
      if (nieuw?.id) this._openOrder(nieuw);
      else await this._load();
    } catch (e) {
      this._error = e.message || 'Labopdracht aanmaken mislukt';
    } finally {
      this._saving = false;
    }
  }

  render() {
    // Labopdrachten horen bij GeoDesk. Het menu-item links staat er niet zonder die licentie,
    // maar het adres blijft bestaan -- een geplakte link hoort uit te leggen wat er mist en niet
    // een leeg scherm te geven.
    if (this._bedrijfsbreed && this._license === 'geovoice') return this._renderGeenLicentie();
    if (this._loading) return html`<div class="muted">Labopdrachten laden...</div>`;
    if (this._error) return this._renderLaadfout();
    if (!this._connections.length) return this._renderNoConnection();
    return this._renderOverview();
  }

  _renderGeenLicentie() {
    return html`
      <section class="empty">
        <div class="empty-icon">${unsafeSVG(lucideIcon(FlaskConical, 28))}</div>
        <h2>Hiervoor is een GeoDesk-licentie nodig</h2>
        <p>Met GeoDesk stel je mengmonsters samen en stuur je ze rechtstreeks naar het laboratorium.</p>
        <a class="btn primary" href="mailto:sales@geovoice.nl?subject=Upgrade%20naar%20GeoDesk">Contact voor upgrade</a>
      </section>
    `;
  }

  /**
   * Opdrachten die bij het lab liggen maar niet uit GeoVoice komen.
   *
   * Apart en niet tussen de rest: ze hebben geen project, geen mengmonsters en geen pagina om
   * naartoe te klikken. Ze staan er omdat de klantcode gedeeld is -- wie via TerraIndex of met
   * de hand in Atmis inschiet, komt hier ook terecht, en dan wil je weten dat het bestaat.
   */
  _renderExtern() {
    const alles = this._externeStatussen;
    const lijst = this._externGefilterd;
    const conn = this._connections.find(c => c.labKey === (alles[0]?.labKey || 'sgs')) || this._connections[0];
    const labnaam = conn?.displayName || 'het lab';
    const klantcode = alles[0]?.customerId || conn?.customerId || '—';
    return html`
      <div class="externblok">
        <div class="paginakop">
          <h2>
            <button class="klap" aria-expanded=${this._externOpen} @click=${() => { this._externOpen = !this._externOpen; }}
                    title=${this._externOpen ? 'Inklappen' : 'Uitklappen'}>
              ${unsafeSVG(lucideIcon(this._externOpen ? ChevronDown : ChevronRight, 18))}
            </button>
            Labopdrachten buiten GeoVoice <em>${alles.length}</em>
          </h2>
          <!-- Ook dichtgeklapt één regel uitleg: zonder die regel zegt de kop niets. -->
          <p>
            Opdrachten die bij ${labnaam} onder klantcode ${klantcode} bekend zijn maar niet via GeoVoice
            zijn aangemaakt (bijvoorbeeld rechtstreeks in het labportaal). Alleen ter info: ze hangen
            niet aan een project.
          </p>
        </div>

        ${!this._externOpen ? '' : html`
          <div class="toolbar">
            <div class="search">
              ${unsafeSVG(lucideIcon(Search, 15))}
              <input .value=${this._externQuery} @input=${e => this._externQuery = e.target.value}
                     placeholder="Zoek ordernummer, batch, project of projectleider">
            </div>
            ${this._haalt ? html`
              <span class="ophalen">
                ${unsafeSVG(lucideIcon(RefreshCw, 14))} Ophalen bij ${labnaam}…
              </span>` : ''}
            <div class="status-group" title="Hoe ver terug we bij het lab kijken. Wat eerder is opgehaald blijft staan.">
              <span>Ophalen over:</span>
              <!-- Het lab geeft zijn opdrachten terug over een venster in dagen. Korter is
                   sneller, langer laat meer zien; @MIS zelf kent geen grens. Wat we ooit
                   opgehaald hebben bewaren we, dus korter kiezen maakt de lijst niet kleiner. -->
              <select .value=${String(this._periode)} ?disabled=${this._haalt}
                      @change=${e => this._zetPeriode(Number(e.target.value))}>
                <option value="60">60 dagen</option>
                <option value="365">12 maanden</option>
                <option value="1825">5 jaar</option>
              </select>
            </div>
            <div class="status-group">
              <span>Labstatus:</span>
              <select .value=${this._externFilter} @change=${e => this._externFilter = e.target.value}>
                <option value="all">Alle</option>
                <option value="warn">Nog te bevestigen</option>
                <option value="bezig">In behandeling</option>
                <option value="ok">Gerapporteerd</option>
                <option value="bad">Afgekeurd</option>
              </select>
            </div>
          </div>

          ${this._haalfout ? html`<div class="notice fout">${this._haalfout}</div>` : ''}
          <div class=${this._haalt ? 'aanhetladen' : ''}>${this._renderExternTabel(lijst)}</div>`}
      </div>
    `;
  }

  _renderExternTabel(lijst) {
    const kolommen = this._zichtbareKolommen(LabKoppelingPage.EXTERN_KOLOMMEN, this._externKolommen);
    const raster = kolommen.map(k => k.breedte).join(' ');
    return html`
      <div class="table">
        <div class="row head extern" style="grid-template-columns:${raster}">
          ${kolommen.map(k => html`
            <span class=${k.id === 'results' ? 'rechts' : ''}>${k.label}</span>`)}
          <column-picker .options=${LabKoppelingPage.EXTERN_KOLOMMEN} .visible=${this._externKolommen}
            @change=${e => this._bewaarKolommen(LabKoppelingPage.EXTERN_KOLOMMEN_PREF, e.detail.visible)}>
          </column-picker>
        </div>
        ${lijst.length ? lijst.map(o => this._renderExternRij(o, kolommen, raster))
          : html`<div class="empty-line">Geen opdrachten die aan de filters voldoen.</div>`}
      </div>
    `;
  }

  _renderExternRij(o, kolommen, raster) {
    const gerapporteerd = /reported|gerapporteerd/i.test(`${o.statusName || ''} ${o.batchStatusName || ''}`);
    const open = this._resultaatVoor === o.orderId;
    const lab = o.labKey === 'sgs' ? 'SGS' : o.labKey;
    const r = o.results;
    const cel = {
      order: html`<span class="nummer">${o.orderId}</span>`,
      project: html`<span class="project" title=${r?.projectName || ''}>${r?.projectCode || r?.projectName || '—'}</span>`,
      projectLeader: html`<span>${r?.projectLeader || '—'}</span>`,
      date: html`<span>${r?.reportedOn ? formatDate(r.reportedOn) : '—'}</span>`,
      labStatus: this._renderLabstatus({ labStatusName: o.statusName || o.statusId, labName: lab }),
      batch: html`<span class="nummer zacht">${o.batchId || '—'}</span>`,
      batchStatus: this._renderLabstatus({ labStatusName: o.batchStatusName, labName: lab }),
      results: html`
        <span class="acties rechts">
          ${o.hasResults ? html`
            <button class="tekstknop klaar" @click=${() => this._toonResultaat(o)}>
              ${open ? 'Verbergen' : 'Bekijk uitslag'}
            </button>
          ` : gerapporteerd ? html`
            <button class="tekstknop" ?disabled=${this._resultaatBezig === o.orderId}
                    @click=${() => this._haalResultaat(o)}>
              ${this._resultaatBezig === o.orderId ? 'Ophalen…' : 'Uitslag ophalen'}
            </button>
          ` : html`<span class="grijs">Nog niet gerapporteerd</span>`}
          ${this._renderCertificaatKnop(o)}
        </span>`,
    };
    return html`
      <div class="row data extern ${open ? 'open' : ''}" style="grid-template-columns:${raster}">
        ${kolommen.map(k => cel[k.id])}
      </div>
      ${open && o.results ? this._renderUitslag(o) : ''}
    `;
  }

  /**
   * Het analysecertificaat als documentknop.
   *
   * Een icoon en geen woord: in een rij met een actie ernaast leest 'Analysecertificaat' als
   * twee knoppen die om dezelfde aandacht vragen, terwijl dit de zijstap is -- het officiële
   * rapport van het lab, bij ons alleen doorgegeven. De uitleg staat in de tooltip.
   */
  _renderCertificaatKnop(o) {
    if (!o.batchId) return '';
    const bezig = this._certBezig === o.orderId;
    return html`
      <button class="doc ${bezig ? 'bezig' : ''}" ?disabled=${bezig}
              @mousemove=${tipMove} @mouseleave=${tipHide}
              data-tip=${`Analysecertificaat ${o.batchId}\nHet analyserapport van het lab, opent in een tabblad`}
              @click=${() => this._haalCertificaat(o)}>
        ${unsafeSVG(lucideIcon(FileText, 16))}
      </button>
    `;
  }

  /**
   * De omschrijving bij een meetwaarde, in het Nederlands.
   *
   * Het resultaatbestand zet er Engels in ('not detected'), terwijl het analysecertificaat van
   * ditzelfde lab 'niet gedetecteerd' afdrukt. Twee woorden voor hetzelfde in twee documenten
   * van dezelfde opdracht is verwarrend, dus volgen we het certificaat. Wat we niet herkennen
   * laten we staan zoals het lab het schreef.
   */
  _meetwaarde(w) {
    const tekst = String(w.valueText || '').trim();
    if (!tekst) return w.value || '—';
    if (/^not detected$/i.test(tekst)) return 'niet gedetecteerd';
    if (/^not applicable$/i.test(tekst)) return 'niet van toepassing';
    return tekst;
  }

  /**
   * De meetwaarden per analysemonster.
   *
   * Zonder toetsing: het lab meet, wij beoordelen niet. Er komt geen enkel veld in het
   * resultaatbestand dat iets over normen zegt -- toetsen tegen achtergrond- en
   * interventiewaarden is een eigen stap, en daar is lutum en organische stof voor nodig.
   */
  _renderUitslag(o) {
    const r = o.results;
    return html`
      <div class="uitslag">
        <div class="uitslagkop">
          <div>
            <strong>${r.projectName || r.projectCode || `Opdracht ${o.orderId}`}</strong>
            <span>certificaat ${r.certificateNumber || '—'} · ${r.samples.length}
                  ${r.samples.length === 1 ? 'analysemonster' : 'analysemonsters'} ·
                  ${r.resultCount} ${r.resultCount === 1 ? 'meetwaarde' : 'meetwaarden'}</span>
          </div>
          <span class="rek"></span>
          <!-- Ook hier bij de hand: je kijkt naar de uitslag en wilt het officiële rapport. -->
          <button class="btn" ?disabled=${this._certBezig === o.orderId}
                  @click=${() => this._haalCertificaat(o)}>
            ${unsafeSVG(lucideIcon(FileText, 14))}
            ${this._certBezig === o.orderId ? 'Openen…' : 'Analysecertificaat'}
          </button>
        </div>
        ${r.samples.map(m => html`
          <div class="uitslagmonster">
            <div class="uitslagmonsterkop">
              <strong>${m.name || '—'}</strong>
              ${m.analysisSampleId ? html`<em>gekoppeld aan ${m.analysisSampleId}</em>`
                : html`<em class="grijs">niet via GeoVoice ingeschoten</em>`}
            </div>
            <table class="meetwaarden">
              <thead>
                <tr>
                  <th>Bepaling</th><th class="num">Meetwaarde</th>
                  <th class="code">Component</th><th class="code">Eenheid</th>
                </tr>
              </thead>
              <tbody>
                ${m.results.map(w => html`
                  <tr>
                    <td>${w.label || w.componentCode || '—'}</td>
                    <td class="num">${this._meetwaarde(w)}</td>
                    <td class="code">${w.componentCode || ''}</td>
                    <!-- Een codelijstnummer, geen 'kg'. Zolang we de lijst van het lab niet
                         hebben tonen we hem als code, want '119' als eenheid is een leugen. -->
                    <td class="code">${w.unitCode || ''}</td>
                  </tr>`)}
              </tbody>
            </table>
          </div>`)}
        <p class="uitslagvoet">
          Dit zijn de meetwaarden uit het resultaatbestand van het lab. Het analysecertificaat
          bevat daarnaast de verantwoording: gebruikte methode en relatie tot de norm, LOQ,
          CAS-nummers, meetonzekerheid en het verificatienummer — die staan niet in het bestand.
          Component- en eenheidnummers zijn codes uit de codelijst van het lab.
        </p>
      </div>
    `;
  }

  /**
   * Het analysecertificaat van het lab openen.
   *
   * Het bestand komt van SGS en wordt niet bij ons opgeslagen: het is hun officiële rapport,
   * en dat hoort daar de bron van te blijven. We halen het op en zetten het in een tabblad.
   */
  async _haalCertificaat(o) {
    this._certBezig = o.orderId;
    this._haalfout = '';
    try {
      const { blob } = await api.downloadLabOrderReport(o.orderId);
      // Openen en niet downloaden: dit is het analyserapport van SGS met hun briefhoofd en
      // handtekening, en dat wil je bekijken -- niet eerst in je downloadmap terugvinden.
      // Bewaren kan alsnog vanuit de PDF-lezer.
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, '_blank', 'noopener');
      if (!tab) {
        // Pop-upblokkering: dan toch maar als download, anders gebeurt er niets.
        const a = document.createElement('a');
        a.href = url;
        a.download = `analysecertificaat-${o.batchId || o.orderId}.pdf`;
        a.click();
      }
      // Even wachten met opruimen: de lezer heeft de URL nog nodig om te tekenen.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      this._haalfout = `Het analysecertificaat kon niet worden opgehaald. ${e.message || ''}`.trim();
    } finally {
      this._certBezig = '';
    }
  }

  async _haalResultaat(o) {
    this._resultaatBezig = o.orderId;
    this._haalfout = '';
    try {
      const res = await api.fetchLabOrderResults(o.orderId);
      const nieuw = res.labStatus;
      this._labStatussen = this._labStatussen.map(s => (s.orderId === nieuw.orderId ? nieuw : s));
      this._resultaatVoor = nieuw.orderId;
    } catch (e) {
      this._haalfout = `De uitslag kon niet worden opgehaald. ${e.message || ''}`.trim();
    } finally {
      this._resultaatBezig = '';
    this._certBezig = '';
    }
  }

  _toonResultaat(o) {
    this._resultaatVoor = this._resultaatVoor === o.orderId ? null : o.orderId;
  }

  /**
   * De lijst kon niet worden geladen.
   *
   * Stond eerst als kale regel in een rood vlak, met de melding die de server gaf -- en bij een
   * storing was dat 'HTTP 503: Service Unavailable'. Daar kan niemand iets mee, en er stond ook
   * niet bij wat je eraan kunt doen. Nu met de vraag erbij die je op dat moment hebt: opnieuw
   * proberen.
   */
  _renderLaadfout() {
    return html`
      <section class="empty">
        <div class="empty-icon">${unsafeSVG(lucideIcon(AlertTriangle, 28))}</div>
        <h2>De labopdrachten konden niet worden geladen</h2>
        <p>${this._error}</p>
        <button class="btn primary" @click=${this._load}>
          ${unsafeSVG(lucideIcon(RefreshCw, 14))} Opnieuw proberen
        </button>
      </section>
    `;
  }

  _renderNoConnection() {
    return html`
      <section class="empty">
        <div class="empty-icon">${unsafeSVG(lucideIcon(FlaskConical, 28))}</div>
        <h2>Geen labkoppeling actief</h2>
        <p>Er is nog geen labkoppeling actief voor deze organisatie. Neem contact op met support@geovoice.nl om dit op te zetten.</p>
        <a class="btn primary" href="mailto:support@geovoice.nl?subject=Labkoppeling activeren">
          ${unsafeSVG(lucideIcon(Mail, 14))} Mail support
        </a>
      </section>
    `;
  }

  _renderOverview() {
    // Geen melding meer over niet-verzonden opdrachten: een concept dat blijft liggen is geen
    // fout. Je maakt er meer aan dan je verstuurt, en een waarschuwing daarover is alleen maar
    // in de weg. Wat wél op jou wacht -- bevestigen in @MIS -- valt op in de fasetabs.
    return html`
      <div class="lab-page">
        <div class="paginakop">
          <!-- De titel alleen op de eigen pagina: in het tabblad staat de projectkop er al
               boven, en twee titels onder elkaar vertellen hetzelfde twee keer. -->
          ${this._bedrijfsbreed ? html`
            <div>
              <h1>Labopdrachten</h1>
              <p>Alle labopdrachten van ${auth.getActiveCompany()?.name || 'dit bedrijf'}, over de projecten heen.</p>
            </div>` : html`<span></span>`}
          <div class="kopacties">
            ${this._bedrijfsbreed ? this._renderStand() : ''}
            <button class="btn primary" @click=${this._openNewOrder} ?disabled=${this._saving}>
              ${unsafeSVG(lucideIcon(Plus, 14))} Nieuwe labopdracht
            </button>
          </div>
        </div>
        ${this._newOpen ? this._renderNewOrderDialog() : ''}

        <!-- Bij een fout blijft de tijd staan: wat je ziet is dan de vorige ronde, en dat is
             precies wat je moet weten. Hem weghalen zou de lijst er vers uit laten zien. -->
        ${this._haalfout ? html`<div class="notice fout">${this._haalfout}</div>` : ''}

        ${this._renderFasetabs()}

        <div class="toolbar">
          <div class="search">
            ${unsafeSVG(lucideIcon(Search, 15))}
            <input .value=${this._query} @input=${e => this._query = e.target.value}
                   placeholder="Zoek opdracht, project, mengmonster, lab of ordernummer">
          </div>
          <!-- Alleen de opdrachten van één of meer projecten; met een zoekveld, want een bedrijf
               heeft er veel. Binnen een project is er maar één, dus daar niet. -->
          ${this._bedrijfsbreed ? html`
            <filter-keuze class="projectkeuze" leeg="Alle projecten" meervoud="projecten" zoekLabel="Zoek project"
                          eenheid="opdrachten" eenheidEen="opdracht"
                          .items=${this._projectFilterOpties} .gekozen=${this._projectFilter}
                          @wijzig=${(e) => { this._projectFilter = e.detail.gekozen; }}></filter-keuze>` : ''}
          <span class="filterlabel">${this._fase === 'alle'
            ? 'Nieuwste eerst'
            : html`Gefilterd op ${FASES[this._fase].label.toLowerCase()} ·
                   <button class="tekstknop" @click=${() => { this._fase = 'alle'; }}>wissen</button>`}</span>
        </div>

        ${this._renderEigenTabel()}

        ${this._bedrijfsbreed && this._externeStatussen.length ? this._renderExtern() : ''}
      </div>
    `;
  }

  /**
   * 'Stand bij het lab: vandaag 16:41', met het knopje om het nu opnieuw te vragen.
   *
   * De stand komt vanzelf zodra de lijst er staat (zie _haalStatussen); tijdens het ophalen
   * draait het icoontje, zodat je weet dat er nog iets kan veranderen.
   */
  _renderStand() {
    const bezig = this._haalt;
    return html`
      <span class="stand">
        ${bezig ? 'Stand bij het lab ophalen…'
          : this._opgehaaldOp ? `Stand bij het lab: ${this._wanneer(this._opgehaaldOp)}`
          : 'Stand bij het lab nog niet opgehaald'}
        <button class="ververs ${bezig ? 'draait' : ''}" @click=${this._verversNu}
                ?disabled=${bezig || this._verversOpSlot}
                title=${bezig ? 'Bezig met ophalen…'
                        : this._verversOpSlot ? 'Net opgehaald — over een paar tellen kun je het weer proberen.'
                        : 'Nu opnieuw bij het lab kijken'}>
          ${unsafeSVG(lucideIcon(RefreshCw, 13))}
        </button>
      </span>`;
  }

  /** 'vandaag 16:41' voor vandaag, anders datum en tijd. */
  _wanneer(waarde) {
    const d = parseTimestamp(waarde);
    if (!d) return '—';
    return d.toDateString() === new Date().toDateString()
      ? `vandaag ${formatTime(waarde)}`
      : formatDateTime(waarde);
  }

  /**
   * De vier fases als tabs, in de volgorde waarin een opdracht ze doorloopt.
   *
   * Ze vervangen de tellers en het statusfilter: het getal zegt hoeveel er staan, de onderregel
   * bij wie de bal ligt, en een klik filtert de lijst. Nog een klik op dezelfde tab zet het
   * filter weer uit. Bevestigen kleurt als er iets staat: dat is het enige waarop de lijst op
   * jou wacht zonder dat je het in GeoVoice ziet.
   */
  _renderFasetabs() {
    // Met een projectfilter tellen alleen de opdrachten van die projecten mee.
    const orders = this._ordersInProjecten;
    const tel = telFases(orders);
    const kies = (fase) => { this._fase = this._fase === fase ? 'alle' : fase; };
    return html`
      <div class="fases">
        <button class="alle ${this._fase === 'alle' ? 'aan' : ''}" @click=${() => { this._fase = 'alle'; }}>
          <strong>${orders.length}</strong><span>Alle</span>
        </button>
        <div class="pijplijn">
          ${FASE_VOLGORDE.map((fase, i) => html`
            <button class="fase ${fase} ${this._fase === fase ? 'aan' : ''} ${tel[fase] ? 'heeft' : ''}"
                    @click=${() => kies(fase)}>
              <strong>${tel[fase]}</strong>
              <span class="faselabel"><b>${FASES[fase].label}</b><small>${FASES[fase].sub}</small></span>
              ${i < FASE_VOLGORDE.length - 1 ? html`<span class="pijl">›</span>` : ''}
            </button>`)}
        </div>
      </div>`;
  }

  /**
   * De tabel met labopdrachten uit GeoVoice.
   *
   * Eén kolom Fase in plaats van een GeoVoice-status en een labstatus naast elkaar: wat je wilt
   * weten is waar een opdracht staat en wat er nu moet gebeuren, en dat staat in de laatste
   * kolom. De hele rij opent de opdracht; de knoppen in de laatste kolom doen hun eigen ding.
   */
  _renderEigenTabel() {
    const lijst = this._filteredOrders;
    return html`
      <div class="table eigen">
        <div class="row head">
          <span>Opdracht</span><span>Projectleider</span><span>Fase</span><span>Lab</span>
          <span>Mengmonsters</span><span>Aangemaakt</span><span class="rechts">Volgende stap</span>
        </div>
        ${lijst.length ? lijst.map(order => this._renderEigenRij(order))
          : html`<div class="empty-line">${!(this._orders || []).length
            ? (this._bedrijfsbreed
              ? 'Nog geen labopdrachten. Je maakt er een aan met de knop hierboven.'
              : 'Nog geen labopdrachten voor dit project.')
            : this._fase !== 'alle' && !this._query.trim()
              ? 'Geen opdrachten in deze fase.'
              : 'Geen opdrachten die aan de zoekterm voldoen.'}</div>`}
      </div>
    `;
  }

  _renderEigenRij(order) {
    const fase = faseVan(order);
    const afgekeurd = isAfgekeurd(order);
    const voortgang = fase === 'concept' ? conceptVoortgang(order) : null;
    const url = this._orderUrl(order, voortgang?.volgende?.stap || '');
    return html`
      <div class="row data ${fase} ${afgekeurd ? 'afgekeurd' : ''}" @click=${() => this._openOrder(order, null, url)}>
        <span class="opdracht">
          <a href=${url} @click=${(e) => { e.stopPropagation(); this._openOrder(order, e, url); }}>${order.number || '—'}</a>
          <small title=${this._projectVan(order)}>${this._projectVan(order)}</small>
        </span>
        <span class="zacht">${order.projectLeaderName || '—'}</span>
        ${this._renderFasecel(order, fase, afgekeurd, voortgang)}
        <span class="zacht">${this._labVan(order)}</span>
        <span class="badges">${(order.badges || []).length
          ? order.badges.map(b => html`<em>${b}</em>`)
          : html`<span class="streep">—</span>`}</span>
        <!-- Altijd de aanmaakdatum: één betekenis voor de hele kolom. Wanneer een opdracht
             verstuurd, bevestigd of gerapporteerd is staat bij de streepjes in de fasekolom. -->
        <span class="zacht datum">${formatDate(order.createdAt)}</span>
        ${this._renderVolgendeStap(order, fase, afgekeurd, voortgang, url)}
      </div>`;
  }

  /** Vier streepjes tot en met de huidige stap, met de fase en een regel uitleg eronder. */
  _renderFasecel(order, fase, afgekeurd, voortgang) {
    const stap = FASES[fase].stap;
    const stand = labStatusPresentatie(order.labStatusName);
    const sub = {
      concept: () => `${voortgang.aantal}/3 · volgende: ${voortgang.volgende.kort}`,
      bevestigen: () => (order.sentAt ? `Verstuurd ${formatDate(order.sentAt)}, nog niet bevestigd` : 'Nog niet bevestigd'),
      lab: () => (afgekeurd ? `${stand?.label || 'Afgekeurd'} door het lab` : stand?.label || 'Bevestigd'),
      resultaten: () => 'Gerapporteerd',
    }[fase]();
    return html`
      <span class="fasecel" @mousemove=${tipMove} @mouseleave=${tipHide}
            data-tip=${stand ? `${this._labVan(order)} · ${stand.label}\n${stand.uitleg}` : ''}>
        <!-- Elk streepje is een stap; aanwijzen zegt wanneer de opdracht daar kwam. -->
        <span class="streepjes">${faseMomenten(order).map((m, i) => html`
          <span class="streepje" data-tip=${this._momentTip(m, afgekeurd && i === 2)}>
            <i class=${i + 1 <= stap ? 'aan' : ''}></i>
          </span>`)}</span>
        <span class="faseregel"><b>${afgekeurd ? 'Afgekeurd' : FASES[fase].label}</b><small>${sub}</small></span>
      </span>`;
  }

  /**
   * De tooltip van één streepje: de stap, en wanneer de opdracht daar kwam. Bij het lab is dat
   * het moment waarop GeoVoice het zag; vóór 29-10-2026 werd dat niet bijgehouden.
   */
  _momentTip(m, afgekeurd) {
    if (!m.bereikt) return `${m.label}\nNog niet`;
    if (afgekeurd) return `${m.label}\nAfgekeurd door het lab`;
    if (!m.moment) return `${m.label}\n${m.gebeurtenis} · tijdstip niet bekend`;
    const gezien = m.fase === 'lab' || m.fase === 'resultaten' ? ' (gezien bij ronde langs het lab)' : '';
    return `${m.label}\n${m.gebeurtenis} op ${formatDateTime(m.moment)}${gezien}`;
  }

  /**
   * Wat er nu moet gebeuren, en door wie. Bij een concept is dat de stap die openstaat; bij
   * bevestigen de knop naar het labportaal; bij het lab niets (dat staat er ook); bij
   * resultaten de link naar de resultaten (daar staat ook het certificaat).
   */
  /** De naam van het lab zoals we hem overal schrijven: 'Eurofins', niet de sleutel 'eurofins'. */
  _labVan(order) {
    return order.labKey ? labPresentation(order.labKey).name : (order.labName || '—');
  }

  _renderVolgendeStap(order, fase, afgekeurd, voortgang, url) {
    const lab = order.labKey ? labPresentation(order.labKey).name : (order.labName || 'het lab');
    const stil = (e) => e.stopPropagation();
    if (fase === 'concept') {
      return html`
        <span class="volgende">
          <a class="actie" href=${url} @click=${(e) => { stil(e); this._openOrder(order, e, url); }}>
            ${voortgang.volgende.label} ${unsafeSVG(lucideIcon(ArrowRight, 14))}
          </a>
        </span>`;
    }
    if (fase === 'bevestigen') {
      const conn = this._connections.find(c => c.id === order.labConnectionId)
        || this._connections.find(c => c.labKey === (order.labKey || 'sgs'));
      const atmis = atmisOrderUrl(conn);
      return html`
        <span class="volgende">
          ${atmis ? html`
            <a class="mis" href=${atmis} target="_blank" rel="noopener noreferrer" @click=${stil}>
              ${unsafeSVG(lucideIcon(ExternalLink, 13))} Openen in @MIS
            </a>` : html`<span class="grijs">Bevestigen bij ${lab}</span>`}
        </span>`;
    }
    if (fase === 'lab') {
      return html`<span class="volgende">${afgekeurd
        ? html`<span class="rood">Neem contact op met ${lab}</span>`
        : html`<span class="grijs">Wacht op ${lab}</span>`}</span>`;
    }
    // Alleen 'Resultaten': het certificaat download je op de pagina van de opdracht zelf.
    return html`
      <span class="volgende">
        <a class="actie" href=${url} @click=${(e) => { stil(e); this._openOrder(order, e, url); }}>
          Resultaten ${unsafeSVG(lucideIcon(ArrowRight, 14))}
        </a>
      </span>`;
  }

  /** Projectnaam zoals de koptekst hem toont, met de code als terugval. */
  /**
   * De projectnaam bij een rij. Bedrijfsbreed komt hij uit de opdracht zelf (de backend joint
   * hem mee); binnen een project van de pagina, want daar is hij voor elke rij dezelfde.
   */
  _projectVan(order) {
    if (order.projectName || order.projectCode) return order.projectName || order.projectCode;
    const p = this.project || {};
    return p.project_name || p.name || p.project_code || p.projectCode || '-';
  }

  _renderNewOrderDialog() {
    const conn = this._connections.find(c => c.id === this._newForm.labConnectionId) || null;
    const labName = (c) => c?.displayName || c?.labKey || 'Lab';
    return html`
      <div class="dialog-overlay" @click=${(e) => e.target === e.currentTarget && (this._newOpen = false)}>
        <div class="dialog">
          <div class="dialog-head">
            <h3>Nieuwe labopdracht</h3>
            <button class="icon-btn" @click=${() => this._newOpen = false}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </div>
          <div class="dialog-body">
            <!-- Alleen bedrijfsbreed: binnen een project staat het project al vast. Een
                 zoeklijst en geen gewone keuzelijst, want een bedrijf heeft er honderden. -->
            ${this._bedrijfsbreed ? html`
              <label>
                <span>Project</span>
                <searchable-select
                  .options=${this._projectOpties}
                  .value=${this._newForm.projectId || ''}
                  placeholder="Zoek een project…"
                  @change=${e => this._newForm = { ...this._newForm, projectId: e.detail.value }}>
                </searchable-select>
              </label>` : ''}
            <label>
              <span>Laboratorium</span>
              <select .value=${this._newForm.labConnectionId} @change=${e => this._setNewLab(e.target.value)}>
                ${this._connections.map(c => html`
                  <option value=${c.id} ?selected=${c.id === this._newForm.labConnectionId}>
                    ${labName(c)}${c.environment === 'test' ? ' (testomgeving)' : ''}
                  </option>`)}
              </select>
            </label>
            <label>
              <span>Monstertype</span>
              <select .value=${this._newForm.sampleType} @change=${e => this._newForm = { ...this._newForm, sampleType: e.target.value }}>
                ${MONSTERTYPES.map(([v, label]) => html`
                  <option value=${v} ?selected=${this._newForm.sampleType === v}>${label}</option>`)}
              </select>
            </label>
            <label>
              <span>Klantcode</span>
              <!-- De klantcode komt van het lab en is niet iets wat je hier verzint: hij is
                   opgehaald toen de koppeling werd geverifieerd. Daarom tonen we hem wel,
                   maar laten we hem niet bewerken. Zodra een bedrijf er meerdere heeft,
                   wordt dit een keuzelijst. -->
              <select disabled>
                <option>${conn?.customerId || 'Nog niet bekend'}</option>
              </select>
            </label>
            ${conn && !conn.customerId ? html`
              <p class="dialog-note">Deze koppeling is nog niet geverifieerd, dus de klantcode is nog onbekend. Een beheerder kan dat doen bij Bedrijven.</p>
            ` : ''}
          </div>
          <div class="dialog-actions">
            <button class="btn" @click=${() => this._newOpen = false}>Annuleren</button>
            <button class="btn primary" @click=${this._createOrder} ?disabled=${this._saving || !this._newForm.labConnectionId}>
              ${unsafeSVG(lucideIcon(Plus, 14))} Aanmaken
            </button>
          </div>
        </div>
      </div>`;
  }


  static styles = css`${unsafeCSS(base)}
    :host { display:block; }
    .lab-page { display:flex; flex-direction:column; gap:0.9rem; }
    .hint { margin:0; font-size:0.78rem; color:var(--mv-text-muted); }
    .statuscel { display:flex; flex-direction:column; gap:2px; align-items:flex-start; }
    .labstatus { color:var(--mv-text-sub); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .klaar { color:var(--mv-primary); }
    .notice.fout { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .btn.bezig svg { animation:draai 1s linear infinite; }
    @keyframes draai { to { transform:rotate(360deg); } }
    /* Geen knop maar een melding: het ophalen gebeurt vanzelf, je hoeft er niets mee. */
    .ophalen { display:flex; align-items:center; gap:7px; font-size:0.78rem; font-weight:600;
               color:var(--mv-text-muted); }
    /* De regel met de tijd, en het knopje om het toch nu te doen. Klein en ernaast, want het
       ophalen gebeurt vanzelf -- dit is de uitzondering, geen hoofdhandeling. */
    .standregel { display:flex; align-items:center; gap:6px; }
    .ververs { display:inline-flex; padding:3px; border:none; border-radius:6px; background:none;
               color:var(--mv-text-muted); cursor:pointer; }
    .ververs:hover:not(:disabled) { background:var(--mv-surface-alt); color:var(--mv-text); }
    .ververs:disabled { opacity:.4; cursor:default; }
    /* Terwijl het lab bevraagd wordt: de lijst die er staat is de oude, en dat hoort te zien
       te zijn -- anders lijkt een andere periode niets te doen. */
    .aanhetladen { opacity:.45; pointer-events:none; transition:opacity .15s; }
    .ophalen svg { animation:draai 1s linear infinite; }

    .externblok { margin-top:1.25rem; }
    .externkop { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; margin-bottom:0.5rem; }
    .externkop .hint { margin:0; }
    .row.extern { grid-template-columns:110px minmax(110px,180px) minmax(100px,150px) 100px 125px 110px 125px minmax(215px,1fr); }
    .row.extern .nummer.zacht { font-weight:600; color:var(--mv-text-sub); }
    .externblok .paginakop h2 { display:flex; align-items:center; gap:9px; margin:0; font-size:1.1rem; }
    /* Het pijltje hoort bij de titel: samen zijn ze de knop die het blok open- en dichtdoet. */
    .klap { display:flex; padding:2px; margin-left:-4px; border:none; background:none;
            color:var(--mv-text-sub); cursor:pointer; border-radius:6px; }
    .klap:hover { background:var(--mv-surface-alt); color:var(--mv-text); }
    /* Het certificaat als documentknop: de zijstap naast de uitslag, niet ernaast in woorden. */
    .doc { display:flex; padding:5px; border:1px solid var(--mv-border); border-radius:7px;
           background:var(--mv-surface); color:var(--mv-text-sub); cursor:pointer; }
    .doc:hover:not(:disabled) { border-color:var(--mv-primary); color:var(--mv-primary); }
    .doc:disabled { opacity:.5; cursor:default; }
    /* Alleen uitlijnen, geen flexbox: een tekstcel die flexkind is krimpt niet mee en liep
       daardoor buiten zijn kolom. Voor de knoppen is flexbox wél nodig. */
    .rechts { text-align:right; }
    .acties.rechts { display:flex; justify-content:flex-end; align-items:center; gap:8px; }
    .externblok .paginakop h2 em { font-style:normal; font-size:0.74rem; font-weight:700;
                                   padding:2px 9px; border-radius:999px;
                                   background:var(--mv-surface-alt); color:var(--mv-text-sub); }
    /* Breed genoeg voor twee regels, en lucht eronder: de zoekbalk plakte tegen de tekst. */
    .externblok .paginakop { margin-bottom:1rem; }
    .externblok .paginakop p { margin:6px 0 0; max-width:105ch; font-size:0.8rem;
                               color:var(--mv-text-sub); line-height:1.5; }
    .acties { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .row.data.extern.open { background:var(--mv-surface-alt); }
    .tekstknop { border:none; background:none; padding:0; font:inherit; font-size:0.78rem;
                 font-weight:700; color:var(--mv-text-sub); cursor:pointer; }
    .tekstknop:hover:not(:disabled) { color:var(--mv-primary); }
    .tekstknop.klaar { color:var(--mv-primary); }
    .tekstknop:disabled { opacity:.5; cursor:default; }
    .grijs { color:var(--mv-text-muted); }

    .uitslag { border:1px solid var(--mv-border); border-top:none; border-radius:0 0 8px 8px;
               background:var(--mv-surface-alt); padding:12px 14px 14px; }
    .uitslagkop { display:flex; align-items:flex-start; gap:10px; margin-bottom:10px; }
    .uitslagkop strong { display:block; font-size:0.9rem; }
    .uitslagkop span { font-size:0.74rem; color:var(--mv-text-muted); }
    .uitslagmonster { background:var(--mv-surface); border:1px solid var(--mv-border);
                      border-radius:8px; padding:10px 12px; margin-top:8px; }
    .uitslagmonsterkop { display:flex; align-items:baseline; gap:8px; margin-bottom:8px; }
    .uitslagmonsterkop strong { font-size:0.84rem; }
    .uitslagmonsterkop em { font-style:normal; font-size:0.7rem; color:var(--mv-text-muted); }
    /* De meetwaarden als tabel en niet als raster van losse regeltjes: een uitslag lees je
       kolom voor kolom, en 'not detected 2540 chrysotiel' achter elkaar is geen uitslag. */
    /* Niet over de volle breedte: dan staat de meetwaarde een halve pagina van zijn bepaling
       af en lees je de verkeerde regel. */
    .meetwaarden { width:100%; max-width:740px; border-collapse:collapse; font-size:0.78rem; }
    .meetwaarden th { padding:5px 8px; text-align:left; font-size:0.64rem; font-weight:800;
                      letter-spacing:.04em; text-transform:uppercase; color:var(--mv-text-muted);
                      border-bottom:1px solid var(--mv-border); white-space:nowrap; }
    .meetwaarden td { padding:5px 8px; border-bottom:1px solid var(--mv-border-soft, var(--mv-border));
                      color:var(--mv-text-sub); }
    .meetwaarden tbody tr:last-child td { border-bottom:none; }
    .meetwaarden td:first-child { color:var(--mv-text); }
    .meetwaarden .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
    .meetwaarden td.num { font-weight:700; color:var(--mv-text); }
    .uitslagvoet { margin:10px 2px 0; max-width:100ch; font-size:0.72rem; line-height:1.5;
                   color:var(--mv-text-muted); }
    .meetwaarden .code { width:90px; font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
                         font-size:0.68rem; color:var(--mv-text-muted); }
    .row.data.extern { background:var(--mv-surface); }
    .row.extern .nummer { font-weight:800; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; }

    .paginakop h1 { margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text); }
    .paginakop p { margin:4px 0 0; font-size:0.9rem; color:var(--mv-text-sub); }
    .muted { color:var(--mv-text-muted); padding:2rem; }
    .error { margin:0 0 0.875rem; padding:0.5rem 0.75rem; border-radius:var(--mv-radius-sm); background:var(--mv-danger-bg); color:var(--mv-danger); font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); }
    .empty { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:320px; text-align:center; border:1px dashed var(--mv-border); border-radius:8px; background:var(--mv-surface); padding:2rem; }
    .empty-icon { color:var(--mv-primary); margin-bottom:0.8rem; }
    .empty h2 { margin:0 0 0.5rem; color:var(--mv-text); font-size:1.2rem; }
    .empty p { max-width:520px; margin:0 0 1.25rem; color:var(--mv-text-sub); line-height:1.55; }
    /* Knop op dezelfde maat als 'Nieuwe boring' in de boringentab: vaste hoogte van 36px met
       horizontale padding, niet verticale padding rond een geerfde tekstgrootte. Die erfde
       hier 15px en werd daarmee zichtbaar hoger en breder dan dezelfde knop een tab verderop. */
    .btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; height:36px;
      border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
      border-radius:var(--mv-radius-sm); padding:0 14px; font-family:inherit;
      font-size:var(--mv-text-base); font-weight:var(--mv-weight-semi); cursor:pointer; text-decoration:none;
      white-space:nowrap; transition:background 0.15s ease, border-color 0.15s ease; }
    .btn.primary { background:var(--mv-primary); border-color:var(--mv-primary); color:#fff; }
    .btn.primary:hover:not(:disabled) { background:var(--mv-primary-hover); border-color:var(--mv-primary-hover); }
    .btn:hover:not(:disabled) { border-color:var(--mv-text-sub); }
    .btn:disabled { opacity:.55; cursor:not-allowed; }

    /* Melding: een regel, geen balk. Hij stond op geerfde 15px vet in een blok met 12px
       padding over de volle breedte, en dat is veel gewicht voor een mededeling die je
       hooguit even wilt zien. Nu valt hij op door kleur en niet door formaat. De kleuren
       lopen via tokens, want hardgecodeerd geel-op-bruin klopt niet in het donkere thema. */
    /* Aanmaakpopup. Bewust smal: drie keuzes, meer niet. */
    .dialog-overlay { position:fixed; inset:0; z-index:var(--mv-z-modal); background:rgba(16,28,18,0.55);
      display:flex; align-items:flex-start; justify-content:center; padding:2.75rem 1.25rem; overflow:auto; }
    .dialog { width:100%; max-width:26rem; background:var(--mv-surface); border-radius:var(--mv-radius-xl);
      box-shadow:0 30px 80px rgba(0,0,0,0.35); overflow:hidden; }
    .dialog-head { display:flex; align-items:center; justify-content:space-between; gap:1rem;
      padding:1.125rem 1.25rem; border-bottom:1px solid var(--mv-border-soft); }
    .dialog-head h3 { margin:0; font-size:var(--mv-text-lg); font-weight:var(--mv-weight-heavy);
      letter-spacing:var(--mv-tracking-snug); color:var(--mv-text); }
    .dialog-body { padding:1.25rem; display:flex; flex-direction:column; gap:0.875rem; }
    .dialog-body label { display:flex; flex-direction:column; gap:0.3rem; }
    .dialog-body label > span { font-size:var(--mv-text-xs); font-weight:var(--mv-weight-heavy);
      letter-spacing:var(--mv-tracking-caps); text-transform:uppercase; color:var(--mv-text-sub); }
    .dialog-body select:disabled { color:var(--mv-text-sub); background:var(--mv-surface-alt); cursor:default; }
    .field-hint { color:var(--mv-text-muted); font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); line-height:1.45; }
    .field-hint a { color:var(--mv-primary); text-decoration:underline; }
    /* Elke groep een eigen tegel op een zacht vlak, in plaats van gescheiden door een streep.
       Wit-op-wit las slecht: de invoervelden zijn zelf wit, dus op een witte ondergrond zag je
       alleen dunne randjes en liep alles in elkaar over. Dezelfde oplossing als bij de lagen in
       een opname. De velden houden hun witte achtergrond (zie de veldregel verderop) en steken
       nu af tegen de tegel. */
    .group { padding:0.875rem 1rem 1rem; background:var(--mv-surface-alt); border:1px solid var(--mv-border); border-radius:10px; }
    .group h3 { margin:0 0 0.75rem; }
    .readonly { display:flex; flex-direction:column; gap:0.25rem; }
    .readonly span { font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); }
    /* Zelfde hoogte als een invoerveld ernaast, anders staat de rij scheef. */
    /* gap: het logovierkantje bij Laboratorium staat anders tegen de naam aan. */
    .readonly strong { display:flex; align-items:center; gap:0.5rem; min-height:2.125rem; font-size:var(--mv-text-md); font-weight:var(--mv-weight-bold); color:var(--mv-text); }
    .matrix-empty { margin:0; padding:1.5rem; text-align:center; color:var(--mv-text-sub); font-size:var(--mv-text-sm); }
    .matrix th .col-name { display:block; max-width:11rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .matrix th code { display:block; margin-top:0.125rem; font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); }
    .picker { max-width:34rem; }
    .picker-sub { margin:0.125rem 0 0; font-size:var(--mv-text-xs); font-weight:var(--mv-weight-heavy); letter-spacing:var(--mv-tracking-caps); text-transform:uppercase; color:var(--mv-text-sub); }
    .picker-input { width:100%; box-sizing:border-box; padding:0.5rem 0.6875rem; border:1.5px solid var(--mv-border); border-radius:var(--mv-radius); font:inherit; font-size:var(--mv-text-md); color:var(--mv-text); background:var(--mv-surface); }
    .picker-input:focus { outline:none; border-color:var(--mv-primary); }
    .picker-error { margin:0.6875rem 0 0; font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); color:var(--mv-danger); }
    .picker-note { margin:1.25rem 0; text-align:center; font-size:var(--mv-text-sm); color:var(--mv-text-sub); }
    /* Vaste hoogte: de lijst mag het venster niet laten springen bij elke toetsaanslag. */
    .picker-results { margin-top:0.6875rem; max-height:19rem; overflow:auto; }
    .picker-row { display:flex; align-items:baseline; justify-content:space-between; gap:0.75rem; width:100%; padding:0.5rem 0.625rem; border:none; border-radius:var(--mv-radius-sm); background:none; text-align:left; font:inherit; cursor:pointer; }
    .picker-row:hover { background:var(--mv-surface-alt); }
    .picker-name { font-size:var(--mv-text-md); font-weight:var(--mv-weight-bold); color:var(--mv-text); }
    .picker-meta { display:flex; align-items:baseline; gap:0.5rem; flex:none; font-size:var(--mv-text-xs); color:var(--mv-text-sub); }
    .picker-meta code { font-weight:var(--mv-weight-heavy); }
    .dialog-note { margin:0; font-size:var(--mv-text-sm); color:var(--mv-warning-text); }
    .dialog-actions { display:flex; justify-content:flex-end; gap:0.5rem;
      padding:0 1.25rem 1.25rem; }
    .icon-btn { width:2rem; height:2rem; border:none; background:none; border-radius:var(--mv-radius-sm);
      display:flex; align-items:center; justify-content:center; color:var(--mv-text-sub); cursor:pointer; }
    .icon-btn:hover { background:var(--mv-surface-alt); }

    /* Dezelfde melding, maar met een knop erin: die moet rechts blijven staan. */
    .notice.bevestig { display:flex; align-items:center; gap:10px; }
    .notice.bevestig span { flex:1; }
    .notice.bevestig .btn { flex:none; text-decoration:none; }
    .notice { display:inline-flex; align-items:center; gap:7px; padding:0.4rem 0.7rem;
      border-radius:var(--mv-radius-sm); border:1px solid var(--mv-warning-panel-border);
      background:var(--mv-warning-panel-bg); color:var(--mv-warning-text);
      font-size:var(--mv-text-sm); font-weight:var(--mv-weight-bold); margin-bottom:0.875rem; }
    .notice svg { flex:none; }
    .overview-head { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:1rem; }
    .metrics { display:flex; gap:0.75rem; flex-wrap:wrap; }
    .metric { min-width:118px; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); padding:0.7rem 0.9rem; background:var(--mv-surface); }
    /* Getal en label allebei op het zwaarste gewicht maakte er vier zwarte blokjes van. Het
       getal is de inhoud en mag opvallen, het label hoort te wijken: lichter, kleiner en in
       de gedempte kleur. Alleen het getal draagt nu gewicht. */
    .metric strong { display:block; font-size:var(--mv-text-xl); font-weight:var(--mv-weight-semi); letter-spacing:var(--mv-tracking-snug); color:var(--mv-text); line-height:1.2; }
    .metric span { display:block; margin-top:2px; color:var(--mv-text-muted); font-size:var(--mv-text-xs); font-weight:var(--mv-weight-semi); letter-spacing:0.02em; }
    .toolbar { display:flex; align-items:center; gap:1rem; margin-bottom:1rem; }
    /* Statuskiezer als smalle dropdown naast de zoekbalk, zelfde vorm als op /projects. Hij
       erfde hierboven de width:100% van de gedeelde veldregel en werd daardoor even breed als
       het zoekveld, terwijl hij maar een handvol korte waarden bevat. */
    .status-group { display:flex; align-items:center; gap:0.5rem; flex-shrink:0; }
    .status-group > span { font-size:var(--mv-text-base); font-weight:var(--mv-weight-semi); color:var(--mv-text-sub); white-space:nowrap; }
    .toolbar select { width:auto; min-width:130px; padding:0 2rem 0 0.75rem; cursor:pointer;
      font-weight:var(--mv-weight-semi); appearance:none; background-repeat:no-repeat;
      /* Zelfde afstand als de formuliervelden hieronder en als .mv-select in base.css. */
      background-position:right 0.75rem center; background-size:1rem;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B978D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E"); }
    .search { flex:1; display:flex; align-items:center; gap:8px; border:1px solid var(--mv-border); border-radius:8px; padding:0 0.75rem; background:var(--mv-surface); color:var(--mv-text-muted); }
    .search input, .toolbar select, label input, label select, textarea { width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); padding:0 0.7rem; height:36px; background:var(--mv-surface); color:var(--mv-text); font-family:inherit; font-size:var(--mv-text-base); }
    textarea { height:auto; padding:0.55rem 0.7rem; }
    /* Dropdowns in de formulieren krijgen dezelfde chevron als de rest van de backoffice; zie
       .mv-select in base.css. Zonder deze regel tekent de browser zijn eigen pijltje, strak
       tegen de rechterrand -- en in een brede tegel valt dat ver van de waarde af te staan. */
    label select { appearance:none; -webkit-appearance:none; padding-right:2rem;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B978D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat:no-repeat; background-position:right 0.75rem center; background-size:1rem; }
    .search { height:36px; }
    /* Even hoog als de zoekbalk. */
    .projectkeuze { flex:none; --fk-hoogte:38px; --fk-letter:var(--mv-text-base); --fk-min:300px; --fk-max:360px; }
    .search input { border:none; padding:0; height:100%; outline:none; }
    .table { border:1px solid var(--mv-border); border-radius:var(--mv-radius-lg); overflow:hidden; background:var(--mv-surface); }
    /* box-sizing: zonder dit telt de padding op bij 100% breedte en steekt de rij 29px buiten
       de tegel uit -- precies genoeg om de laatste kolom af te kappen. */
    .row { box-sizing:border-box; display:grid; grid-template-columns:minmax(120px,200px) minmax(105px,150px) 115px 110px 105px 90px 100px 75px 95px minmax(120px,170px) 55px minmax(90px,1fr); gap:10px; align-items:center; padding:0.6875rem 0.9rem; border-bottom:1px solid var(--mv-border-soft); text-align:left; color:var(--mv-text); font-size:var(--mv-text-base); }
    /* Zonder min-width:0 rekt een lange naam zijn kolom op en schuift de laatste kolom
       voorbij de rand van de tegel. */
    .row > * { min-width:0; overflow:hidden; text-overflow:ellipsis; }
    .row > .badges, .row > .acties, .row > .statuscel, .row > .labstatus,
    .row > column-picker { overflow:visible; }
    .row.head { position:relative; background:var(--mv-surface-alt); font-size:0.76rem; font-weight:800; color:var(--mv-text-sub); }
    /* Het tandwiel hangt in de koprij en staat buiten het raster: zo schuift het niet mee met
       de kolommen en houdt de laatste kop zijn plek.
       GEEN transform om te centreren: een getransformeerd element wordt het referentiekader
       voor zijn position:fixed-kinderen, en dan valt het uitklapmenu alsnog binnen de tabel --
       die overflow:hidden heeft voor haar ronde hoeken, dus dan zie je bij het klikken niets
       gebeuren. Verticaal centreren gaat daarom met flex over de volle hoogte. */
    .row.head column-picker { position:absolute; right:0.45rem; top:0; bottom:0;
                              display:flex; align-items:center; }
    .row.head > span:last-of-type { padding-right:26px; }
    a.row { width:100%; border-width:0 0 1px; background:var(--mv-surface); cursor:pointer;
            font-family:inherit; font-size:var(--mv-text-base); text-decoration:none; }
    a.row:hover { background:var(--mv-surface-alt); }
    .row .project { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .pill { display:inline-flex; border-radius:999px; padding:2px 8px; font-size:0.74rem; font-weight:800; }
    .pill.ok { background:var(--mv-primary-tint); color:var(--mv-primary); }
    .pill.warn { background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); }
    .pill.bad { background:var(--mv-danger-bg,#fff5f5); color:var(--mv-danger,#dc3545); }
    /* Voor de stand die het lab meldt: in bewerking is blauw (er gebeurt iets), net binnen is
       grijs (er gebeurt nog niets). Gerapporteerd en afgekeurd delen de kleuren hierboven. */
    .pill.bezig { background:var(--mv-info-bg,#eef4fd); color:var(--mv-info-text,#1c5fa8); }
    .pill.neutraal { background:var(--mv-surface-alt); color:var(--mv-text-sub); }
    .labstatus .leeg { font-style:normal; color:var(--mv-text-muted); }
    .badges { display:flex; flex-wrap:wrap; gap:4px; }
    .badges em { font-style:normal; background:var(--mv-primary-tint); color:var(--mv-primary); border-radius:5px; padding:2px 6px; font-size:0.75rem; font-weight:800; }
    .badges small, .empty-line { color:var(--mv-text-muted); }
    .empty-line { padding:2rem; text-align:center; }
    .detail-head { display:flex; align-items:center; gap:0.8rem; margin-bottom:1rem; }
    .detail-head h2 { margin:0; font-size:1.2rem; color:var(--mv-text); }
    .detail-head p { margin:0.15rem 0 0; color:var(--mv-text-sub); font-size:0.85rem; }
    .head-actions { margin-left:auto; display:flex; gap:0.5rem; }
    .icon-btn { width:34px; height:34px; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text); border-radius:8px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
    .tabs { display:flex; gap:0; border-bottom:1px solid var(--mv-border); margin-bottom:1rem; }
    .tabs button { border:none; background:none; color:var(--mv-text-sub); padding:0.75rem 1rem; font-weight:800; cursor:pointer; border-bottom:2px solid transparent; }
    .tabs button.active { color:var(--mv-primary); border-bottom-color:var(--mv-primary); }
    /* Houder van de tegels: zelf wit, met ruimte ertussen zodat de groepen los van elkaar
       leesbaar blijven in plaats van als één blok. */
    .panel { display:flex; flex-direction:column; gap:0.875rem; border:1px solid var(--mv-border); border-radius:8px; padding:1rem; background:var(--mv-surface); }
    .form-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:1rem; }
    /* Twee velden in één roostercel, onder elkaar. Nodig voor het e-mailadres van de
       projectleider: als eigen cel schoof dat naar de volgende rij, helemaal links, en dan
       stond het los van het veld waar het bij hoort. */
    .stack { display:flex; flex-direction:column; gap:0.75rem; }
    /* Eigen element: erft de regel voor input/select hierboven niet, dus hier op dezelfde
       hoogte zetten als de velden ernaast. */
    .form-grid searchable-select { display:block; --ss-text-transform:none; }
    .form-grid searchable-select::part(trigger) {
      height:36px; padding:0 0.7rem; border-radius:var(--mv-radius-sm); font-size:var(--mv-text-base);
    }
    label span, .wide-label { display:block; margin-bottom:0.35rem; color:var(--mv-text-sub); font-size:0.78rem; font-weight:800; }
    textarea { min-height:120px; resize:vertical; }
    .actions { display:flex; justify-content:flex-end; gap:0.5rem; margin-top:1rem; }
    .actions.split { justify-content:flex-start; flex-wrap:wrap; }
    .composition { display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:1rem; }
    .volle-breedte { grid-column:1 / -1; }
    .profile-area, .side-box, .matrix { border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); }
    .profile-toolbar, .side-title, .matrix-head { display:flex; align-items:center; justify-content:space-between; gap:8px; padding:0.75rem 0.9rem; border-bottom:1px solid var(--mv-border); color:var(--mv-text); font-weight:800; }
    .profile-toolbar span { color:var(--mv-text-sub); font-size:0.8rem; }
    .profiles { display:flex; gap:0.75rem; overflow-x:auto; padding:1rem; align-items:flex-start; min-height:280px; }
    .boring-col { width:116px; flex:0 0 116px; }
    .boring-title { font-size:0.8rem; font-weight:900; color:var(--mv-text); margin-bottom:0.5rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .layers { position:relative; border-left:2px solid var(--mv-border); background:var(--mv-surface-alt); border-radius:6px; overflow:hidden; }
    .layer { position:absolute; left:7px; right:0; border:1px solid rgba(0,0,0,0.12); border-radius:5px; padding:4px; display:flex; flex-direction:column; align-items:flex-start; justify-content:center; color:#17201c; cursor:pointer; overflow:hidden; font:inherit; text-align:left; }
    .layer.selected { outline:3px solid var(--mv-primary); z-index:3; }
    .layer span { font-size:0.68rem; font-weight:900; line-height:1.1; }
    .layer small { font-size:0.65rem; font-weight:700; opacity:.8; }
    .sample-side { display:flex; flex-direction:column; gap:1rem; min-width:0; }
    .side-box { padding-bottom:0.75rem; }
    .side-box p { margin:0.75rem 0.9rem 0; color:var(--mv-text-sub); font-size:0.88rem; line-height:1.45; }
    .sample-card { display:flex; align-items:center; justify-content:space-between; gap:8px; margin:0.7rem 0.75rem 0; padding:0.7rem; border:1px solid var(--mv-border); border-radius:8px; }
    .sample-card strong { display:block; color:var(--mv-text); }
    .sample-card span { color:var(--mv-text-sub); font-size:0.78rem; }
    .sample-card button { border:none; background:none; color:var(--mv-danger,#dc3545); cursor:pointer; display:flex; }
    .matrix { grid-column:1 / -1; overflow:auto; }
    table { width:100%; border-collapse:collapse; }
    th, td { border-top:1px solid var(--mv-border); padding:0.65rem 0.8rem; color:var(--mv-text); text-align:left; }
    th { background:var(--mv-surface-alt); color:var(--mv-text-sub); font-size:0.78rem; }
    .check { min-width:58px; border:1px solid var(--mv-border); border-radius:7px; background:var(--mv-surface); color:var(--mv-text-sub); padding:0.35rem 0.55rem; cursor:pointer; font-weight:800; }
    .check.on { background:var(--mv-primary-tint); color:var(--mv-primary); border-color:var(--mv-primary); }
    /* ── Fases ──────────────────────────────────────────────────────────────
       Vier kleuren, één per fase, en overal dezelfde: in de tabs, de streepjes in de rij en
       de stappenbalk op de pagina van een opdracht (lab-order-page). De kleuren staan als
       --fase-* in tokens.css. */
    .lab-page > .paginakop { display:flex; align-items:flex-end; justify-content:space-between; gap:1.5rem; }
    .kopacties { display:flex; align-items:center; gap:1rem; flex-wrap:wrap; justify-content:flex-end; }
    .stand { display:inline-flex; align-items:center; gap:4px; font-size:0.8rem; color:var(--mv-text-muted); white-space:nowrap; }
    .ververs.draait svg { animation:draai 1s linear infinite; }

    .fases { display:flex; gap:12px; align-items:stretch; }
    .fases button { font-family:inherit; cursor:pointer; text-align:left; }
    .fases strong { font-size:1.4rem; font-weight:700; letter-spacing:-0.02em; color:var(--mv-text); line-height:1.1; }
    .fases .alle { flex:none; width:120px; display:flex; flex-direction:column; gap:4px; padding:12px 16px;
                   border:1px solid var(--mv-border); border-radius:14px; background:transparent; }
    .fases .alle span { font-size:0.8rem; font-weight:600; color:var(--mv-text-sub); }
    .fases .alle.aan { background:var(--mv-surface); border-color:var(--mv-primary); }
    .pijplijn { flex:1; min-width:0; display:flex; gap:2px; padding:5px; border:1px solid var(--mv-border);
                border-radius:14px; background:var(--mv-surface); }
    .fase { flex:1; min-width:0; display:flex; align-items:center; gap:12px; padding:9px 14px; border:0;
            border-radius:10px; background:transparent; }
    .fase:hover { background:var(--mv-surface-alt); }
    .fase strong { min-width:22px; }
    .faselabel { min-width:0; display:flex; flex-direction:column; gap:2px; }
    .faselabel b { font-size:0.82rem; font-weight:600; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .faselabel small { font-size:0.75rem; color:var(--mv-text-sub); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .fase .pijl { margin-left:auto; color:var(--mv-text-muted); font-size:1.1rem; }
    .fase.bevestigen.heeft strong { color:var(--fase-bevestigen-tekst); }
    .fase.concept.aan { background:var(--fase-concept-tint); }
    .fase.bevestigen.aan { background:var(--fase-bevestigen-tint); }
    .fase.lab.aan { background:var(--fase-lab-tint); }
    .fase.resultaten.aan { background:var(--fase-resultaten-tint); }
    .fase.aan strong { color:var(--fase-concept-tekst); }
    .fase.bevestigen.aan strong { color:var(--fase-bevestigen-tekst); }
    .fase.lab.aan strong { color:var(--fase-lab-tekst); }
    .fase.resultaten.aan strong { color:var(--fase-resultaten-tekst); }

    .filterlabel { flex:none; font-size:0.8rem; color:var(--mv-text-sub); white-space:nowrap; }

    /* De eigen tabel: vaste kolommen, de volgende stap rechts. */
    .table.eigen .row { grid-template-columns:minmax(0,1.5fr) minmax(90px,140px) minmax(200px,270px) 70px minmax(0,1fr) 92px minmax(200px,250px);
                        gap:16px; padding:0.8rem 1.25rem; }
    .table.eigen .row.head { font-size:0.8rem; font-weight:600; }
    .table.eigen .row.data { cursor:pointer; background:var(--mv-surface); }
    .table.eigen .row.data:hover { background:var(--mv-surface-alt); }
    .table.eigen .row.data.bevestigen { background:color-mix(in srgb, var(--mv-warning-panel-bg) 35%, var(--mv-surface)); }
    .table.eigen .row.data.bevestigen:hover { background:color-mix(in srgb, var(--mv-warning-panel-bg) 60%, var(--mv-surface)); }
    .row > .fasecel, .row > .volgende, .row > .opdracht { overflow:visible; }
    .opdracht { display:flex; flex-direction:column; gap:2px; min-width:0; }
    .opdracht a { font-weight:600; color:var(--mv-text); text-decoration:none; font-variant-numeric:tabular-nums; }
    .opdracht a:hover { color:var(--mv-primary); }
    .opdracht small { font-size:0.8rem; color:var(--mv-text-sub); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .zacht { color:var(--mv-text-sub); }
    .datum { font-variant-numeric:tabular-nums; }
    .streep { color:var(--mv-text-muted); }
    .fasecel { display:flex; flex-direction:column; gap:6px; min-width:0; }
    .streepjes { display:flex; gap:3px; }
    /* Het streepje is 4px hoog; de rand eromheen maakt het aanwijsbaar. */
    .streepje { display:block; padding:5px 0; margin:-5px 0; cursor:default; }
    .streepjes i { display:block; width:30px; height:4px; border-radius:2px; background:var(--mv-border); }
    .concept .streepjes i.aan { background:var(--fase-concept); }
    .bevestigen .streepjes i.aan { background:var(--fase-bevestigen); }
    .lab .streepjes i.aan { background:var(--fase-lab); }
    .resultaten .streepjes i.aan { background:var(--fase-resultaten); }
    .afgekeurd .streepjes i.aan { background:var(--mv-danger); }
    .faseregel { display:flex; align-items:baseline; gap:8px; min-width:0; white-space:nowrap; }
    .faseregel b { font-weight:600; }
    .faseregel small { font-size:0.75rem; color:var(--mv-text-muted); overflow:hidden; text-overflow:ellipsis; }
    .concept .faseregel b { color:var(--fase-concept-tekst); }
    .bevestigen .faseregel b { color:var(--fase-bevestigen-tekst); }
    .lab .faseregel b { color:var(--fase-lab-tekst); }
    .resultaten .faseregel b { color:var(--fase-resultaten-tekst); }
    .afgekeurd .faseregel b { color:var(--mv-danger); }
    .volgende { display:flex; justify-content:flex-end; align-items:center; gap:10px; min-width:0; }
    .volgende .actie { display:inline-flex; align-items:center; gap:4px; font-weight:600; color:var(--mv-primary-hover);
                       text-decoration:none; white-space:nowrap; }
    .volgende .actie:hover { color:var(--mv-primary); text-decoration:underline; }
    .volgende .mis { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border:1px solid var(--mv-warning-panel-border);
                     border-radius:8px; background:var(--mv-surface); color:var(--mv-warning-text); font-size:0.8rem;
                     font-weight:600; text-decoration:none; white-space:nowrap; }
    .volgende .mis:hover { background:var(--mv-warning-panel-bg); }
    .volgende .grijs { font-size:0.8rem; }
    .volgende .rood { font-size:0.8rem; font-weight:600; color:var(--mv-danger); }
    .badges em { font-size:0.72rem; }

    @media (max-width: 1200px) {
      .faselabel small { display:none; }
    }
    @media (max-width: 980px) {
      .fases, .pijplijn { flex-direction:column; }
      .fases .alle { width:auto; }
      .fase .pijl { display:none; }
      .lab-page > .paginakop { flex-direction:column; align-items:stretch; }
      .table.eigen .row { grid-template-columns:1fr; gap:6px; }
      .volgende { justify-content:flex-start; }
      .filterlabel { white-space:normal; }
      .composition { grid-template-columns:1fr; }
      .form-grid { grid-template-columns:1fr; }
      .row { grid-template-columns:1fr; }
      .row.head { display:none; }
      .head-actions, .overview-head, .toolbar { flex-direction:column; align-items:stretch; }
      /* In een kolom zou flex:1 de HOOGTE verdelen: de zoekbalk werd een streepje. Hier dus
         een vaste hoogte, en het projectfilter over de hele breedte. */
      .toolbar .search { flex:none; }
      .projectkeuze { --fk-min:100%; --fk-max:none; }
    }
  `;
}

customElements.define('lab-koppeling-page', LabKoppelingPage);
